
import { useState, useEffect } from 'react';
import { EarlyAccessLead } from '@/types/early-access';

export const useLeadsFilters = (leads: EarlyAccessLead[]) => {
  const [filteredLeads, setFilteredLeads] = useState<EarlyAccessLead[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [interviewFilter, setInterviewFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [timeFilter, setTimeFilter] = useState('all');
  const [dateRange, setDateRange] = useState<{ from?: Date; to?: Date }>({});

  const applyFilters = () => {
    let filtered = [...leads];
    
    console.log('Applying filters to', leads.length, 'leads');

    // Apply search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(lead => 
        lead.first_name.toLowerCase().includes(searchLower) ||
        lead.email.toLowerCase().includes(searchLower) ||
        lead.motivation.toLowerCase().includes(searchLower) ||
        (lead.motivation_other && lead.motivation_other.toLowerCase().includes(searchLower)) ||
        lead.experience.toLowerCase().includes(searchLower) ||
        (lead.current_habit_app && lead.current_habit_app.toLowerCase().includes(searchLower)) ||
        lead.challenge.toLowerCase().includes(searchLower) ||
        lead.use_case.toLowerCase().includes(searchLower) ||
        (lead.use_case_other && lead.use_case_other.toLowerCase().includes(searchLower))
      );
    }

    // Apply interview interest filter
    if (interviewFilter !== 'all') {
      if (interviewFilter === 'interested') {
        filtered = filtered.filter(lead => lead.interview_interest === "Yes, I'm interested");
      } else if (interviewFilter === 'not-interested') {
        filtered = filtered.filter(lead => lead.interview_interest !== "Yes, I'm interested");
      }
    }

    // Apply quick date filter
    if (dateFilter !== 'all') {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const yesterday = new Date(today);
      yesterday.setDate(today.getDate() - 1);
      const thisWeek = new Date(today);
      thisWeek.setDate(today.getDate() - 7);
      const thisMonth = new Date(today);
      thisMonth.setMonth(today.getMonth() - 1);

      filtered = filtered.filter(lead => {
        const leadDate = new Date(lead.created_at);
        switch (dateFilter) {
          case 'today':
            return leadDate >= today;
          case 'yesterday':
            return leadDate >= yesterday && leadDate < today;
          case 'week':
            return leadDate >= thisWeek;
          case 'month':
            return leadDate >= thisMonth;
          default:
            return true;
        }
      });
    }

    // Apply date range filter
    if (dateRange.from || dateRange.to) {
      filtered = filtered.filter(lead => {
        const leadDate = new Date(lead.created_at);
        const leadDateOnly = new Date(leadDate.getFullYear(), leadDate.getMonth(), leadDate.getDate());
        
        if (dateRange.from && dateRange.to) {
          const fromDate = new Date(dateRange.from.getFullYear(), dateRange.from.getMonth(), dateRange.from.getDate());
          const toDate = new Date(dateRange.to.getFullYear(), dateRange.to.getMonth(), dateRange.to.getDate());
          return leadDateOnly >= fromDate && leadDateOnly <= toDate;
        } else if (dateRange.from) {
          const fromDate = new Date(dateRange.from.getFullYear(), dateRange.from.getMonth(), dateRange.from.getDate());
          return leadDateOnly >= fromDate;
        } else if (dateRange.to) {
          const toDate = new Date(dateRange.to.getFullYear(), dateRange.to.getMonth(), dateRange.to.getDate());
          return leadDateOnly <= toDate;
        }
        return true;
      });
    }

    // Apply time filter
    if (timeFilter !== 'all') {
      filtered = filtered.filter(lead => {
        const leadDate = new Date(lead.created_at);
        const hour = leadDate.getHours();
        
        switch (timeFilter) {
          case 'dawn':
            return hour >= 0 && hour < 6;
          case 'morning':
            return hour >= 6 && hour < 12;
          case 'afternoon':
            return hour >= 12 && hour < 18;
          case 'evening':
            return hour >= 18 && hour < 24;
          default:
            return true;
        }
      });
    }

    console.log('Filtered results:', filtered.length, 'out of', leads.length);
    setFilteredLeads(filtered);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setInterviewFilter('all');
    setDateFilter('all');
    setTimeFilter('all');
    setDateRange({});
  };

  const hasActiveFilters = Boolean(searchTerm) || 
    interviewFilter !== 'all' || 
    dateFilter !== 'all' || 
    timeFilter !== 'all' ||
    Boolean(dateRange.from) || 
    Boolean(dateRange.to);

  useEffect(() => {
    applyFilters();
  }, [leads, searchTerm, interviewFilter, dateFilter, timeFilter, dateRange]);

  return {
    filteredLeads,
    searchTerm,
    setSearchTerm,
    interviewFilter,
    setInterviewFilter,
    dateFilter,
    setDateFilter,
    timeFilter,
    setTimeFilter,
    dateRange,
    setDateRange,
    hasActiveFilters,
    clearFilters
  };
};
